/**
 * Values shared by the Worker and the React client.
 *
 * Everything in this module is pure data with no runtime dependency on either
 * side, so it can be imported from both bundles without leaking server code
 * into the browser.
 */

/** Limits and lifetimes of the email verification flow, in seconds. */
export const VERIFICATION_CODE_TTL_SECONDS = 600
export const VERIFICATION_CODE_RESEND_COOLDOWN_SECONDS = 60
export const VERIFICATION_CODE_DAILY_LIMIT = 10
export const VERIFICATION_CODE_MAX_ATTEMPTS = 5
/**
 * Grace period before an expired code is deleted. The daily send budget is
 * counted over a rolling 24 hours, so codes must outlive that window.
 */
export const VERIFICATION_CODE_RETENTION_SECONDS = 24 * 60 * 60

/**
 * Session lifetime: thirty days.
 *
 * The cookie carrying the session must expire at the same moment as the stored
 * row, so both are derived from this one constant. A mismatch produces the worst
 * of both worlds: a cookie the server rejects, or a live session the browser has
 * already dropped.
 */
export const SESSION_TTL_SECONDS = 30 * 24 * 60 * 60

/**
 * Session cookie name.
 *
 * Shared by every service under the registrable domain, so it lives here rather
 * than in one service's configuration.
 */
export const SESSION_COOKIE_NAME = 'toh_session'

/** Invite codes: default lifetime, shape and resend budget. */
export const ACTIVATION_CODE_TTL_HOURS_DEFAULT = 72
export const ACTIVATION_CODE_TTL_HOURS_MIN = 1
export const ACTIVATION_CODE_TTL_HOURS_MAX = 24 * 30
export const ACTIVATION_CODE_LENGTH = 12
export const ACTIVATION_CODE_RESEND_COOLDOWN_SECONDS = 60
export const ACTIVATION_CODE_RESEND_DAILY_LIMIT = 10

/** Delivery state of the mail carrying an invite code. */
export const MAIL_STATUSES = ['pending', 'sent', 'failed'] as const
export type MailStatus = (typeof MAIL_STATUSES)[number]

/**
 * Referral codes.
 *
 * A referral code is public: it travels in share links and must never be usable
 * as an invite code, so the two live in separate tables and have different
 * lengths.
 */
export const REFERRAL_CODE_LENGTH = 8
/** How long a captured referral code stays eligible on a device, in seconds. */
export const REFERRAL_CAPTURE_TTL_SECONDS = 7 * 24 * 60 * 60
/** Registrations without a valid code are attributed to this account. */
export const DEFAULT_REFERRER_EMAIL = 'vanfer@vanfer.tech'
/** Where a referral code was captured. `direct` marks the fallback binding. */
export const REFERRAL_CHANNELS = ['event_share', 'site', 'direct'] as const
export type ReferralChannel = (typeof REFERRAL_CHANNELS)[number]

/** Field lengths enforced on both sides of the wire. */
export const LIMITS = {
  emailMaxLength: 254,
  nicknameMinLength: 1,
  nicknameMaxLength: 24,
  eventTitleMinLength: 2,
  eventTitleMaxLength: 80,
  locationLabelMaxLength: 120,
  commentMaxLength: 1000,
  noteMaxLength: 500,
  maxActivitiesPerEvent: 6,
  maxTravelMinutes: 240,
  defaultMaxTravelMinutes: 60,
  /**
   * Distinct departure points one event may have.
   *
   * The ceiling is a quota decision: every departure point is one journey per
   * candidate per accepted mode, and the request sent to the decision model
   * carries one line per point. Participant headcount is unlimited, because a
   * group of ten costs the same as a group of one.
   */
  maxDeparturesPerEvent: 20,
  /** Point at which members are warned that the ceiling is approaching. */
  departureWarningThreshold: 16,
  /** Largest headcount one registration may declare. */
  maxHeadcountPerRegistration: 20,
  /** Free text an applicant may attach to an invite request. */
  applicationReasonMaxLength: 300,
  /** Rejection or approval note an administrator writes. */
  decisionNoteMaxLength: 300,
  banReasonMaxLength: 200,
  activationCodeNoteMaxLength: 120,
  adminNoteMaxLength: 200,
  /** Longest accepted referral code, used to reject oversized junk early. */
  referralCodeMaxLength: 16,
} as const

/** Hard constraint thresholds used by the recommendation engine. */
export const RECOMMENDATION = {
  /**
   * How many candidates are kept per round.
   *
   * Six is also the number of regions the coarse screen evaluates, so every region
   * that survives the free screen is verified and shown: the group decides among the
   * whole shortlist instead of among the three nearest of it.
   */
  topN: 6,
  /** Minimum number of POIs of one activity type that counts as "covered". */
  minPoiPerActivity: 1,
  /** JEV answers below this confidence are flagged for manual review. */
  lowConfidenceThreshold: 0.45,
  /**
   * Minimum gap between two rounds for the same event, in seconds.
   *
   * A round is the most expensive operation the deployment performs: one run
   * spends hundreds of metered map calls, so a repeat click must not buy a
   * second full fan-out.
   */
  runCooldownSeconds: 60,
  /**
   * Upper bounds accepted for the administrator-editable round budgets.
   *
   * The budgets are what keeps a member from buying unlimited provider calls, so
   * a mistyped settings value must not remove the ceiling entirely.
   */
  roundBudgetMax: 10_000,
} as const

/** Who asked for a round. `creator` is the organizer, `member` any participant. */
export const RECOMMENDATION_TRIGGER_SOURCES = ['creator', 'member'] as const
export type RecommendationTriggerSource = (typeof RECOMMENDATION_TRIGGER_SOURCES)[number]


/* ------------------------------------------------------------------ */
/* Administration                                                      */
/* ------------------------------------------------------------------ */

/**
 * Administrators are accounts plus a role. Roles are presets over capabilities,
 * so a single administrator can be given an exception without inventing a role.
 */
export const ADMIN_ROLES = ['owner', 'admin', 'reviewer'] as const
export type AdminRole = (typeof ADMIN_ROLES)[number]

export const ADMIN_CAPABILITIES = [
  'admin:read',
  'admin:manage',
  'application:read',
  'application:decide',
  'code:read',
  'code:issue',
  'code:revoke',
  'user:read',
  'user:activate',
  'user:ban',
  'referral:read',
  'settings:read',
  'settings:write',
] as const
export type AdminCapability = (typeof ADMIN_CAPABILITIES)[number]

/**
 * Effective capabilities of a role.
 *
 * `owner` is expressed as the full list so that a capability added later cannot
 * be silently missing from the account that is supposed to hold everything.
 */
export const ADMIN_ROLE_CAPABILITIES: Record<AdminRole, readonly AdminCapability[]> = {
  owner: ADMIN_CAPABILITIES,
  admin: [
    'application:read',
    'application:decide',
    'code:read',
    'code:issue',
    'code:revoke',
    'user:read',
    'user:activate',
    'referral:read',
    'settings:read',
    'settings:write',
  ],
  reviewer: ['application:read', 'application:decide', 'code:read', 'user:read'],
}

export const ADMIN_STATUSES = ['active', 'disabled'] as const
export type AdminStatus = (typeof ADMIN_STATUSES)[number]

/* ------------------------------------------------------------------ */
/* Invite applications                                                 */
/* ------------------------------------------------------------------ */

export const APPLICATION_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'revoked',
] as const
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number]

/**
 * Preset rejection reasons.
 *
 * The form offers these plus a free text field, because the reason is shown to
 * the applicant: a categorised reason makes repeats less likely than silence.
 */
export const REJECT_REASON_PRESETS = [
  'incomplete_profile',
  'email_unavailable',
  'duplicate_request',
  'suspected_bot',
  'other',
] as const

/* ------------------------------------------------------------------ */
/* Suspension                                                          */
/* ------------------------------------------------------------------ */

/**
 * "Permanent" suspension, in days.
 *
 * Suspension is stored as an expiry and nothing else, so a permanent ban is
 * simply a very distant one. Ten years outlives any account in this product.
 */
export const BAN_PERMANENT_DAYS = 3650

/* ------------------------------------------------------------------ */
/* Settings                                                            */
/* ------------------------------------------------------------------ */

/**
 * Upper bound accepted for the daily registration limit.
 *
 * The limit is a coarse brake on scripted sign ups rather than a per address
 * quota, so the ceiling only has to keep a mistyped value from removing the
 * brake entirely.
 */
export const DAILY_REGISTRATION_LIMIT_MAX = 100_000

/**
 * How the deployment is allowed to call the map vendors.
 *
 * `full` is the ordinary setting. `readonly` stops every outbound map call and
 * leaves rounds working from what is already stored, which is the lever an operator
 * pulls when a quota alert arrives or a vendor starts failing.
 */
export const MAP_CALLS_MODES = ['full', 'readonly'] as const
export type MapCallsMode = (typeof MAP_CALLS_MODES)[number]

/** Upper bound for the per-provider daily map allowance an operator may set. */
export const MAP_DAILY_BUDGET_MAX = 100_000

/** Settings an administrator can change without a deploy. */
export const SETTING_KEYS = {
  inviteApplyEnabled: 'invite_apply_enabled',
  inviteRejectCooldownDays: 'invite_reject_cooldown_days',
  activationCodeTtlHours: 'activation_code_ttl_hours',
  dailyRegistrationLimit: 'daily_registration_limit',
  recommendRoundBudgetPerEvent: 'recommend_round_budget_per_event',
  recommendRoundBudgetPerMember: 'recommend_round_budget_per_member',
  recommendRoundBudgetPerAccountDaily: 'recommend_round_budget_per_account_daily',
  mapCallsMode: 'map_calls_mode',
  mapDailyBudgetPerProvider: 'map_daily_budget_per_provider',
} as const
export type SettingKey = (typeof SETTING_KEYS)[keyof typeof SETTING_KEYS]

export const SETTING_DEFAULTS = {
  inviteApplyEnabled: true,
  inviteRejectCooldownDays: 0,
  activationCodeTtlHours: ACTIVATION_CODE_TTL_HOURS_DEFAULT,
  /**
   * New accounts one deployment may create per local day.
   *
   * Zero is a deliberate setting rather than a missing value: it closes
   * registration for the rest of the day, and the limit is what a deployment
   * raises when a scripted sign up wave has to be absorbed.
   */
  dailyRegistrationLimit: 1000,
  /**
   * Rounds one event may ever compute, counted per paid round rather than per
   * click: a repeat of unchanged inputs is answered from the stored round and
   * costs nothing, so it must not consume the budget either.
   */
  recommendRoundBudgetPerEvent: 100,
  /** Paid rounds one account may buy for one event. */
  recommendRoundBudgetPerMember: 30,
  /** Paid rounds one account may buy across every event in a day. */
  recommendRoundBudgetPerAccountDaily: 30,
  /**
   * Map vendors are called by default; an operator turns this to read-only when a
   * quota alert arrives or a vendor starts failing.
   */
  mapCallsMode: 'full',
  /**
   * Calls one map vendor may serve per local day.
   *
   * The personal keys behind this deployment publish small daily allowances, so the
   * deployment keeps a stricter ceiling of its own: a hundred calls a day is enough
   * to fill the scopes a round needs while leaving room for the next activity.
   */
  mapDailyBudgetPerProvider: 100,
} as const

/** Internal progress kept by scheduled jobs. Never editable from a page. */
export const SYSTEM_STATE_KEYS = {
  referralClickAggregatedUntil: 'referral_click_aggregated_until',
} as const
export type SystemStateKey = (typeof SYSTEM_STATE_KEYS)[keyof typeof SYSTEM_STATE_KEYS]
